import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authHeaders } from './listPageStyles';

// Epic C — "Sorular" tasarımcısı (mockup anket-tasarim-araci.html'e uyumlu).
// 3 kolon: sol palet | orta kanvas (kartlarda CEVAP ÖNİZLEMESİ) | sağ panel (INLINE
// ayarlar + PreviewSummary). Popup/modal YOK; kart seçince sağ panelde canlı (debounced
// autosave) düzenlenir. Palet→kanvas sürükle-bırak + tıkla-ekle; kart sürükle-sırala.

const COLORS = {
  ink: '#1E1B3A', inkSoft: '#4B4670', indigo: '#4F46E5', indigoDeep: '#3730A3',
  indigoSoft: '#EEF0FF', indigoBorder: '#D8D9FB', ember: '#FB7C3C', emberSoft: '#FFE9DD',
  paper: '#FAFAFD', line: '#E7E6F3', white: '#FFFFFF', good: '#1AA37A', goodSoft: '#E3F7EE',
  danger: '#C2455C', dangerSoft: '#FDF3F4', muted: '#A9A5C9',
};

const CHOICE_TYPES = ['List', 'SingleChoice', 'MultiChoice'];
const LINKED_TYPES = ['Product', 'ProductPrice', 'ProductQty', 'User', 'ProductAvailability'];
const SELECTION_TYPES = ['List', 'Product', 'ProductPrice', 'ProductQty', 'User'];
// Bulunurluk alanları (Bölüm 6.6–6.8): girdi + Var/Yok + (Var ise) koşullu foto.
const AVAILABILITY_TYPES = ['ProductAvailability', 'TextAvailability'];

const FIELD_CATEGORIES = [
  { label: 'Temel alanlar', items: [
    { type: 'SectionHeader', label: 'Başlık (Bölüm)' },
    { type: 'Text', label: 'Kısa Metin' },
    { type: 'LongText', label: 'Uzun Metin' },
    { type: 'Numeric', label: 'Numerik' },
    { type: 'Date', label: 'Tarih' },
    { type: 'List', label: 'Liste' },
    { type: 'Signature', label: 'İmza' },
    { type: 'Photo', label: 'Fotoğraf' },
  ] },
  { label: 'Bağlantılı alanlar', items: [
    { type: 'Product', label: 'Ürün', linked: true },
    { type: 'ProductPrice', label: 'Ürün + Fiyat', linked: true },
    { type: 'ProductQty', label: 'Ürün + Miktar', linked: true },
    { type: 'User', label: 'Kullanıcı', linked: true },
    { type: 'ProductAvailability', label: 'Ürün - Bulunurluk', linked: true },
    { type: 'TextAvailability', label: 'Metin - Bulunurluk' },
  ] },
  { label: 'Girdi maskesi', items: [
    { type: 'Money', label: 'Para (Tutar)' },
    { type: 'Plate', label: 'Plaka' },
    { type: 'TcNo', label: 'T.C. No' },
    { type: 'QrCode', label: 'QR / Barkod' },
  ] },
];
const TYPE_LABEL = {};
FIELD_CATEGORIES.forEach((c) => c.items.forEach((i) => { TYPE_LABEL[i.type] = i.label; }));
TYPE_LABEL.SingleChoice = 'Tek Seçim (eski)';
TYPE_LABEL.MultiChoice = 'Çoklu Seçim (eski)';

const TYPE_SWATCH = {
  SectionHeader: '#7C7BEA', Text: '#7C7BEA', LongText: '#7C7BEA',
  Numeric: '#3FA9E0', Date: '#3FA9E0',
  List: '#E0A23F', SingleChoice: '#E0A23F', MultiChoice: '#E0A23F',
  Signature: '#9D6BD1', Photo: '#D1709D',
  Product: '#1AA37A', ProductPrice: '#1AA37A', ProductQty: '#1AA37A', User: '#E0764F',
  ProductAvailability: '#1AA37A', TextAvailability: '#7C7BEA',
  Money: '#3FA9E0', Plate: '#64748b', TcNo: '#64748b', QrCode: '#64748b',
};
const CURRENCY_OPTIONS = [{ value: 'TRY', label: '₺ TRY' }, { value: 'USD', label: '$ USD' }, { value: 'EUR', label: '€ EUR' }];
const CURRENCY_SYMBOL = { TRY: '₺', USD: '$', EUR: '€' };
const LINKED_SOURCE_LABEL = { Product: 'Ürün listesi', ProductPrice: 'Ürün listesi', ProductQty: 'Ürün listesi', User: 'Temsilci listesi', ProductAvailability: 'Ürün listesi' };
const TYPE_HINT = {
  SectionHeader: 'Veri toplamaz; sahada ayraç başlık olarak gösterilir.',
  LongText: 'Sahada çok satırlı metin kutusu (sınırsız).',
  Signature: 'Sahada imza çizim alanı; görsel olarak kaydedilir.',
  Date: 'Sahada tarih seçici.',
  Money: 'Sahada tutar girişi (para birimiyle).',
  Plate: 'Sahada plaka maskesi (34 ABC 123).',
  TcNo: 'Sahada 11 haneli T.C. No.',
  QrCode: 'Sahada QR/barkod okutma.',
  ProductAvailability: 'Sahada ürün seçilir, Var/Yok işaretlenir; "Var" ise foto alanı açılır.',
  TextAvailability: 'Sahada metin girilir, Var/Yok işaretlenir; "Var" ise foto alanı açılır.',
};

// Mockup'taki lucide ikonları.
const IC = {
  layers: <><polygon points="12 2 22 8.5 12 15 2 8.5 12 2" /><polyline points="2 15.5 12 22 22 15.5" /></>,
  type: <><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></>,
  alignLeft: <><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></>,
  hash: <><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
  pen: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
  package: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><polyline points="3.3 7 12 12 20.7 7" /><line x1="12" y1="22" x2="12" y2="12" /></>,
  tag: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>,
  cart: <><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  wallet: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></>,
  qr: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><line x1="14" y1="14" x2="21" y2="21" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  checkSquare: <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  chevDown: <polyline points="6 9 12 15 18 9" />,
  eyeOff: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>,
  grip: <><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></>,
  shieldCheck: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11.5 14.5 16 10" /></>,
  camera: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
};
const TYPE_ICON = {
  SectionHeader: 'layers', Text: 'type', LongText: 'alignLeft', Numeric: 'hash', Date: 'calendar',
  List: 'list', Signature: 'pen', Photo: 'image',
  Product: 'package', ProductPrice: 'tag', ProductQty: 'cart', User: 'user',
  ProductAvailability: 'shieldCheck', TextAvailability: 'shieldCheck',
  Money: 'wallet', Plate: 'type', TcNo: 'type', QrCode: 'qr',
  SingleChoice: 'list', MultiChoice: 'checkSquare',
};
function Svg({ name, size = 14, color, fill = 'none' }) {
  const body = IC[name];
  if (!body) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color || 'currentColor'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{body}</svg>
  );
}
function TypeBadge({ type, size = 26 }) {
  const sw = TYPE_SWATCH[type] || '#94a3b8';
  return (
    <span style={{ width: size, height: size, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: sw + '1c', color: sw, flexShrink: 0 }}>
      <Svg name={TYPE_ICON[type] || 'list'} size={size * 0.56} color={sw} />
    </span>
  );
}
function Pill({ tone, children }) {
  const map = {
    ember: { bg: COLORS.emberSoft, fg: '#C2632F' },
    good: { bg: COLORS.goodSoft, fg: '#0F7A57' },
    indigo: { bg: COLORS.indigoSoft, fg: COLORS.indigoDeep },
  };
  const c = map[tone] || map.indigo;
  return <span style={{ display: 'inline-block', background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999 }}>{children}</span>;
}

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(p) { return pick(p, 'Data', 'data') ?? p; }
function parseConfig(json) { if (!json) return {}; try { return JSON.parse(json) || {}; } catch { return {}; } }

function optionListOf(q) {
  let opts = pick(q, 'options', 'Options');
  if (Array.isArray(opts) && opts.length) return opts.map((o) => ({ id: pick(o, 'id', 'Id'), text: pick(o, 'text', 'Text', 'OptionText') || '' }));
  try { const arr = JSON.parse(pick(q, 'optionsJson', 'OptionsJson') || '[]'); if (Array.isArray(arr)) return arr.map((t) => ({ text: t })); } catch { /* yok */ }
  return [];
}

const inputBoxStyle = { border: `1px solid ${COLORS.line}`, borderRadius: 8, padding: '9px 12px', fontSize: 13.5, background: COLORS.paper, color: COLORS.ink, boxSizing: 'border-box' };
const fieldInputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${COLORS.indigoBorder}`, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { fontSize: 12, fontWeight: 700, color: COLORS.inkSoft, display: 'block', marginBottom: 6 };

function PreviewInput({ placeholder, icon, width }) {
  return (
    <div style={{ ...inputBoxStyle, width: width || 220, display: 'flex', alignItems: 'center', gap: 7, color: COLORS.muted }}>
      {icon && <Svg name={icon} size={13} color={COLORS.muted} />}{placeholder}
    </div>
  );
}

// Bulunurluk önizlemesi: girdi (ürün seçici / metin) + Var/Yok butonları +
// "Var" seçilince koşullu foto alanı (Bölüm 7.1.3.3). Önizleme amaçlı yerel state.
function AvailabilityPreview({ q }) {
  const type = pick(q, 'questionType', 'QuestionType');
  const cfg = parseConfig(pick(q, 'optionsConfigJson', 'OptionsConfigJson'));
  const [picked, setPicked] = useState(null);
  const isProduct = type === 'ProductAvailability';
  return (
    <div>
      {isProduct ? (
        <div style={{ ...inputBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 260, color: COLORS.muted, marginBottom: 10 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Svg name="package" size={13} color={COLORS.muted} /> Ürün seçiniz</span><Svg name="chevDown" size={14} color={COLORS.muted} />
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}><PreviewInput placeholder="Açıklama yazınız" width={260} /></div>
      )}
      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6F6A99', marginBottom: 6 }}>Mevcut mu?</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[{ k: 'var', l: 'Var', on: COLORS.good, soft: COLORS.goodSoft }, { k: 'yok', l: 'Yok', on: COLORS.danger, soft: COLORS.dangerSoft }].map((o) => (
          <button key={o.k} type="button" onClick={(e) => { e.stopPropagation(); setPicked(o.k); }}
            style={{ padding: '7px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              border: picked === o.k ? `1.5px solid ${o.on}` : `1px solid ${COLORS.line}`,
              background: picked === o.k ? o.soft : COLORS.white, color: picked === o.k ? o.on : COLORS.inkSoft }}>{o.l}</button>
        ))}
      </div>
      {picked === 'var' && (
        <div style={{ marginTop: 10, padding: '10px 12px', background: COLORS.indigoSoft, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ ...inputBoxStyle, height: 46, width: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.muted, background: COLORS.white }}><Svg name="camera" size={18} color={COLORS.muted} /></div>
          <div style={{ fontSize: 12, color: COLORS.indigoDeep, lineHeight: 1.4 }}>
            "Var" → foto alanı açıldı {cfg.photoRequired && <Pill tone="ember">Foto zorunlu</Pill>}
          </div>
        </div>
      )}
    </div>
  );
}

// Kartta sahada nasıl görüneceğinin önizlemesi (mockup FieldPreviewBody).
function FieldPreview({ q }) {
  const type = pick(q, 'questionType', 'QuestionType');
  const opts = (q._opts || []).filter((o) => (o.text || '').trim() !== '');
  const multi = (pick(q, 'selectionMode', 'SelectionMode') || 'Single') === 'Multi';
  switch (type) {
    case 'Text': return <PreviewInput placeholder="Kısa cevap" />;
    case 'LongText': return <div style={{ ...inputBoxStyle, height: 50, color: COLORS.muted }}>Uzun cevap</div>;
    case 'Numeric': return <PreviewInput placeholder="0" icon="hash" width={120} />;
    case 'Date': return <PreviewInput placeholder="gg.aa.yyyy" icon="calendar" width={160} />;
    case 'Money': return <PreviewInput placeholder={`${CURRENCY_SYMBOL[parseConfig(pick(q, 'optionsConfigJson', 'OptionsConfigJson')).currency] || '₺'} Tutar`} icon="wallet" width={150} />;
    case 'Plate': return <PreviewInput placeholder="34 ABC 123" width={150} />;
    case 'TcNo': return <PreviewInput placeholder="T.C. Kimlik No" width={170} />;
    case 'QrCode': return <PreviewInput placeholder="QR / Barkod okut" icon="qr" width={180} />;
    case 'Signature': return <div style={{ ...inputBoxStyle, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.muted, gap: 6 }}><Svg name="pen" size={14} color={COLORS.muted} /> İmza alanı</div>;
    case 'Photo': return <div style={{ ...inputBoxStyle, height: 60, width: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.muted, flexDirection: 'column', gap: 4 }}><Svg name="image" size={18} color={COLORS.muted} /><span style={{ fontSize: 11 }}>Fotoğraf</span></div>;
    case 'List': case 'SingleChoice': case 'MultiChoice': {
      const display = opts.length ? opts : [{ text: 'Seçenek 1' }];
      return (
        <div>
          {display.slice(0, 4).map((o, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
              {multi ? <Svg name="checkSquare" size={15} color="#C2BEDC" /> : <span style={{ width: 15, height: 15, borderRadius: '50%', border: '1.5px solid #C2BEDC', display: 'inline-block', flexShrink: 0 }} />}
              <span style={{ fontSize: 13, color: opts.length ? COLORS.ink : COLORS.muted }}>{o.text}</span>
            </div>
          ))}
          {display.length > 4 && <div style={{ fontSize: 12, color: COLORS.muted }}>+{display.length - 4} seçenek daha</div>}
        </div>
      );
    }
    case 'Product': case 'ProductPrice': case 'ProductQty':
      return (
        <div>
          <div style={{ ...inputBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 260, color: COLORS.muted, marginBottom: type !== 'Product' ? 8 : 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Svg name="package" size={13} color={COLORS.muted} /> Ürün seçiniz</span><Svg name="chevDown" size={14} color={COLORS.muted} />
          </div>
          {type === 'ProductPrice' && <PreviewInput placeholder="₺ Fiyat" width={130} />}
          {type === 'ProductQty' && <PreviewInput placeholder="Adet" width={100} />}
          <div style={{ marginTop: 8 }}><Pill tone="good">Ürün listesi</Pill></div>
        </div>
      );
    case 'User':
      return (
        <div>
          <div style={{ ...inputBoxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 260, color: COLORS.muted, marginBottom: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}><Svg name="user" size={13} color={COLORS.muted} /> Kullanıcı seçiniz</span><Svg name="chevDown" size={14} color={COLORS.muted} />
          </div>
          <Pill tone="ember">Temsilci listesi</Pill>
        </div>
      );
    case 'ProductAvailability': case 'TextAvailability':
      return <AvailabilityPreview q={q} />;
    default: return null;
  }
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      style={{ width: 38, height: 22, borderRadius: 999, border: 'none', cursor: 'pointer', background: checked ? COLORS.indigo : '#cbd5e1', position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
    </button>
  );
}

export default function SurveyQuestionsDesigner({ surveyId, showToast, readOnly = false }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);          // kaydedilmemiş değişiklik var mı
  const [savingState, setSavingState] = useState('idle');
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [paletteOver, setPaletteOver] = useState(false);
  const [sourceLists, setSourceLists] = useState({ product: [], user: [] });
  const dragRef = useRef(null);

  const API = `/api/surveys/${surveyId}`;

  const selectQuestion = useCallback((q) => {
    const cfg = parseConfig(pick(q, 'optionsConfigJson', 'OptionsConfigJson'));
    setSelectedId(pick(q, 'id', 'Id'));
    setDirty(false);
    setSavingState('idle');
    setForm({
      questionType: pick(q, 'questionType', 'QuestionType') || 'Text',
      questionText: pick(q, 'questionText', 'QuestionText') || '',
      selectionMode: pick(q, 'selectionMode', 'SelectionMode') || 'Single',
      optionList: q._opts ? q._opts.map((o) => ({ ...o })) : optionListOf(q),
      currency: cfg.currency || 'TRY',
      multiplePhoto: !!cfg.multiple,
      photoRequired: !!cfg.photoRequired,
      sourceListId: cfg.sourceListId || 'all',
      minValue: pick(q, 'minValue', 'MinValue') ?? '',
      maxValue: pick(q, 'maxValue', 'MaxValue') ?? '',
      isRequired: pick(q, 'isRequired', 'IsRequired') ?? false,
      isVisible: pick(q, 'isVisible', 'IsVisible') ?? true,
      sortOrder: pick(q, 'sortOrder', 'SortOrder') ?? 0,
    });
  }, []);

  const fetchQuestions = useCallback(async (selectNewest = false) => {
    try {
      const res = await fetch(`${API}/questions`, { headers: authHeaders() });
      const body = unwrap(await res.json());
      const items = (pick(body, 'items', 'Items') || []).map((q) => ({ ...q, _opts: optionListOf(q) }));
      setQuestions(items);
      if (selectNewest && items.length) selectQuestion(items[items.length - 1]);
      return items;
    } catch (err) { showToast?.('error', err.message || 'Sorular alınamadı'); return []; }
  }, [API, showToast, selectQuestion]);

  useEffect(() => { (async () => { setLoading(true); await fetchQuestions(); setLoading(false); })(); }, [fetchQuestions]);

  // Kaynak Listeler (Bölüm 6.1) — bağlantılı alan dropdown'ı için.
  useEffect(() => {
    (async () => {
      try {
        const [pr, ur] = await Promise.all([
          fetch(`/api/surveys/source-lists?type=product`, { headers: authHeaders() }),
          fetch(`/api/surveys/source-lists?type=user`, { headers: authHeaders() }),
        ]);
        const pb = unwrap(await pr.json());
        const ub = unwrap(await ur.json());
        setSourceLists({
          product: pick(pb, 'items', 'Items') || [],
          user: pick(ub, 'items', 'Items') || [],
        });
      } catch { /* sessiz — dropdown 'Tüm ...' varsayılanıyla çalışır */ }
    })();
  }, []);

  const setF = (patch) => { if (readOnly) return; setForm((c) => ({ ...c, ...patch })); setDirty(true); };

  const buildPayload = (f) => {
    const isSection = f.questionType === 'SectionHeader';
    const cfg = {};
    if (f.questionType === 'Money') cfg.currency = f.currency || 'TRY';
    if (f.questionType === 'Photo' && f.multiplePhoto) cfg.multiple = true;
    if (AVAILABILITY_TYPES.includes(f.questionType) && f.photoRequired) cfg.photoRequired = true;
    if (LINKED_TYPES.includes(f.questionType) && f.sourceListId && f.sourceListId !== 'all') cfg.sourceListId = f.sourceListId;
    return {
      sortOrder: f.sortOrder,
      questionText: f.questionText.trim() || (isSection ? 'Bölüm Başlığı' : 'Soru'),
      questionType: f.questionType,
      selectionMode: SELECTION_TYPES.includes(f.questionType) ? f.selectionMode : null,
      isRequired: isSection ? false : !!f.isRequired,
      isVisible: !!f.isVisible,
      options: CHOICE_TYPES.includes(f.questionType) ? f.optionList.map((o, i) => ({ text: o.text, sortOrder: i })) : null,
      optionsJson: null,
      optionsConfigJson: Object.keys(cfg).length ? JSON.stringify(cfg) : null,
      minValue: f.minValue === '' ? null : Number(f.minValue),
      maxValue: f.maxValue === '' ? null : Number(f.maxValue),
    };
  };

  const saveSelected = useCallback(async (f, qid) => {
    if (readOnly) return false;
    setSavingState('saving');
    try {
      const res = await fetch(`${API}/questions/${qid}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(buildPayload(f)) });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(pick(b, 'Message', 'message') || 'Kaydedilemedi'); }
      setQuestions((qs) => qs.map((q) => (pick(q, 'id', 'Id') === qid ? {
        ...q,
        questionText: f.questionText, QuestionText: f.questionText,
        questionType: f.questionType, QuestionType: f.questionType,
        selectionMode: f.selectionMode, SelectionMode: f.selectionMode,
        isRequired: f.isRequired, IsRequired: f.isRequired,
        isVisible: f.isVisible, IsVisible: f.isVisible,
        optionsConfigJson: buildPayload(f).optionsConfigJson,
        _opts: f.optionList,
      } : q)));
      setSavingState('saved');
      setDirty(false);
      return true;
    } catch (err) { setSavingState('idle'); showToast?.('error', err.message || 'Kaydedilemedi'); return false; }
  }, [API, showToast, readOnly]);

  // Manuel kayıt — kullanıcı "Kaydet"e basınca mevcut soruyu kaydeder.
  const saveCurrent = () => { if (selectedId && form) saveSelected(form, selectedId); };

  // Başka soruya geçerken kaydedilmemiş değişiklik varsa kullanıcıya sor (veri kaybı olmasın).
  const trySelect = (q) => {
    if (selectedId && dirty && form) {
      // eslint-disable-next-line no-alert
      const ans = window.confirm('Kaydedilmemiş değişiklikler var. Kaydedilsin mi?');
      if (ans) { saveSelected(form, selectedId).then(() => selectQuestion(q)); return; }
    }
    selectQuestion(q);
  };

  const nextOrder = () => questions.reduce((m, q) => Math.max(m, pick(q, 'sortOrder', 'SortOrder') || 0), 0) + 1;

  const addType = async (type) => {
    if (readOnly) return;
    const payload = {
      sortOrder: nextOrder(),
      questionText: type === 'SectionHeader' ? 'Yeni Bölüm Başlığı' : `Yeni ${TYPE_LABEL[type] || 'Soru'}`,
      questionType: type,
      selectionMode: SELECTION_TYPES.includes(type) ? 'Single' : null,
      isRequired: false, isVisible: true,
      options: CHOICE_TYPES.includes(type) ? [{ text: 'Seçenek 1', sortOrder: 0 }] : null,
      optionsJson: null, optionsConfigJson: null, minValue: null, maxValue: null,
    };
    try {
      const res = await fetch(`${API}/questions`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(pick(b, 'Message', 'message') || 'Eklenemedi'); }
      await fetchQuestions(true);
    } catch (err) { showToast?.('error', err.message || 'Eklenemedi'); }
  };

  const duplicateSelected = async () => {
    if (readOnly || !form) return;
    const payload = { ...buildPayload(form), sortOrder: nextOrder(), questionText: `${form.questionText} (kopya)` };
    try {
      const res = await fetch(`${API}/questions`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Çoğaltılamadı');
      await fetchQuestions(true);
    } catch (err) { showToast?.('error', err.message || 'Çoğaltılamadı'); }
  };

  const deleteSelected = async () => {
    if (readOnly || !selectedId) return;
    try {
      const res = await fetch(`${API}/questions/${selectedId}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Silinemedi');
      setSelectedId(null); setForm(null);
      await fetchQuestions();
      showToast?.('success', 'Soru silindi.');
    } catch (err) { showToast?.('error', err.message || 'Silinemedi'); }
  };

  const questionToPayload = (q, overrides) => {
    const cfg = parseConfig(pick(q, 'optionsConfigJson', 'OptionsConfigJson'));
    return {
      sortOrder: pick(q, 'sortOrder', 'SortOrder') ?? 0,
      questionText: pick(q, 'questionText', 'QuestionText') || 'Soru',
      questionType: pick(q, 'questionType', 'QuestionType') || 'Text',
      selectionMode: pick(q, 'selectionMode', 'SelectionMode') || null,
      isRequired: pick(q, 'isRequired', 'IsRequired') ?? false,
      isVisible: pick(q, 'isVisible', 'IsVisible') ?? true,
      options: (q._opts || []).map((o, i) => ({ text: o.text, sortOrder: i })),
      optionsConfigJson: Object.keys(cfg).length ? JSON.stringify(cfg) : null,
      minValue: pick(q, 'minValue', 'MinValue') ?? null,
      maxValue: pick(q, 'maxValue', 'MaxValue') ?? null,
      ...overrides,
    };
  };

  const persistOrder = async (ordered) => {
    setQuestions(ordered.map((q, i) => ({ ...q, sortOrder: i + 1, SortOrder: i + 1 })));
    const updates = ordered
      .map((q, i) => ({ q, want: i + 1 }))
      .filter(({ q, want }) => (pick(q, 'sortOrder', 'SortOrder') ?? 0) !== want)
      .map(({ q, want }) => fetch(`${API}/questions/${pick(q, 'id', 'Id')}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(questionToPayload(q, { sortOrder: want })) }));
    try { await Promise.all(updates); } catch { showToast?.('error', 'Sıralama kaydedilemedi'); }
  };

  const onCardDrop = (fromIdx, toIdx) => {
    if (readOnly || fromIdx == null || fromIdx === toIdx) return;
    const next = [...questions];
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    persistOrder(next);
  };

  const summary = useMemo(() => ({
    total: questions.filter((q) => pick(q, 'questionType', 'QuestionType') !== 'SectionHeader').length,
    required: questions.filter((q) => pick(q, 'isRequired', 'IsRequired')).length,
    sections: questions.filter((q) => pick(q, 'questionType', 'QuestionType') === 'SectionHeader').length,
    linked: questions.filter((q) => LINKED_TYPES.includes(pick(q, 'questionType', 'QuestionType'))).length,
  }), [questions]);

  const updateOption = (i, text) => setF({ optionList: form.optionList.map((o, idx) => (idx === i ? { ...o, text } : o)) });
  const addOption = () => setF({ optionList: [...form.optionList, { text: '' }] });
  const removeOption = (i) => setF({ optionList: form.optionList.length > 1 ? form.optionList.filter((_, idx) => idx !== i) : form.optionList });

  if (loading) return <div className="dps-empty"><span>Yükleniyor…</span></div>;

  // Seçili kartın önizlemesi canlı olsun diye form'dan türetilmiş soru-benzeri nesne
  const formAsQuestion = form ? {
    questionType: form.questionType, selectionMode: form.selectionMode,
    optionsConfigJson: buildPayload(form).optionsConfigJson, _opts: form.optionList,
  } : null;

  const isSection = form?.questionType === 'SectionHeader';
  const showSelection = form && SELECTION_TYPES.includes(form.questionType);
  const showOptions = form && CHOICE_TYPES.includes(form.questionType);
  const showLinked = form && LINKED_TYPES.includes(form.questionType);
  const showAvailability = form && AVAILABILITY_TYPES.includes(form.questionType);
  const showRange = form && (form.questionType === 'Numeric' || form.questionType === 'Money');

  return (
    <div className="dps-form" style={{ display: 'grid', gridTemplateColumns: '220px 1fr 300px', gap: 16, alignItems: 'start' }}>
      {/* Sol palet */}
      <div style={{ position: 'sticky', top: 8, display: 'flex', flexDirection: 'column', gap: 14, background: COLORS.white, border: `1px solid ${COLORS.line}`, borderRadius: 12, padding: 14 }}>
        {readOnly && (
          <div style={{ fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
            Salt okunur — soru ekleme/düzenleme kapalı.
          </div>
        )}
        {!readOnly && FIELD_CATEGORIES.map((cat) => (
          <div key={cat.label}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8B86B5', letterSpacing: 0.6, textTransform: 'uppercase', margin: '2px 4px 8px' }}>{cat.label}</div>
            <div>
              {cat.items.map((t) => (
                <div key={t.type} draggable
                  onDragStart={(e) => { dragRef.current = { kind: 'palette', type: t.type }; e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', t.type); }}
                  onDragEnd={() => { dragRef.current = null; setPaletteOver(false); }}
                  onClick={() => addType(t.type)}
                  title={`Sürükleyip kanvasa bırakın ya da tıklayın — "${t.label}"`}
                  className="sqd-pal-item"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 9px', borderRadius: 9, cursor: 'grab', userSelect: 'none', marginBottom: 2 }}>
                  <TypeBadge type={t.type} size={28} />
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink, flex: 1 }}>{t.label}</span>
                  {t.linked && <Svg name="link" size={12} color="#B3AEDD" />}
                </div>
              ))}
            </div>
          </div>
        ))}
        <style>{'.sqd-pal-item:hover{background:' + COLORS.indigoSoft + ';}'}</style>
      </div>

      {/* Orta kanvas (palet→kanvas drop zone) */}
      <div
        onDragOver={(e) => { if (dragRef.current?.kind === 'palette') { e.preventDefault(); setPaletteOver(true); } }}
        onDragLeave={(e) => { if (e.currentTarget === e.target) setPaletteOver(false); }}
        onDrop={(e) => { if (dragRef.current?.kind === 'palette') { e.preventDefault(); addType(dragRef.current.type); dragRef.current = null; setPaletteOver(false); } }}
        style={{ minHeight: 200, borderRadius: 12, border: paletteOver ? `2px dashed ${COLORS.indigo}` : '2px dashed transparent', background: paletteOver ? COLORS.indigoSoft : 'transparent', padding: paletteOver ? 6 : 0, transition: 'background .15s' }}
      >
        {questions.length === 0 ? (
          <div style={{ border: `1.5px dashed ${COLORS.indigoBorder}`, borderRadius: 14, padding: '50px 20px', textAlign: 'center', color: COLORS.muted }}>
            <div style={{ fontWeight: 600, color: '#8B86B5', fontSize: 14 }}>Henüz soru eklenmedi</div>
            <div style={{ fontSize: 12.5, marginTop: 5 }}>Soldaki alan tiplerini buraya sürükleyin ya da tıklayın</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {questions.map((q, idx) => {
              const qid = pick(q, 'id', 'Id');
              const type = pick(q, 'questionType', 'QuestionType');
              const hidden = (pick(q, 'isVisible', 'IsVisible') ?? true) === false;
              const sec = type === 'SectionHeader';
              const sel = selectedId === qid;
              const text = sel && form ? form.questionText : pick(q, 'questionText', 'QuestionText');
              const required = sel && form ? form.isRequired : pick(q, 'isRequired', 'IsRequired');
              const previewQ = sel && formAsQuestion ? formAsQuestion : q;
              const dropTop = dragOverIndex === idx;
              const commonDrag = {
                draggable: !readOnly,
                onDragStart: (e) => { dragRef.current = { kind: 'card', index: idx }; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(idx)); },
                onDragOver: (e) => { e.preventDefault(); if (dragRef.current?.kind === 'card') setDragOverIndex(idx); },
                onDrop: (e) => { e.preventDefault(); e.stopPropagation(); if (dragRef.current?.kind === 'palette') addType(dragRef.current.type); else if (dragRef.current?.kind === 'card') onCardDrop(dragRef.current.index, idx); dragRef.current = null; setDragOverIndex(null); setPaletteOver(false); },
                onDragEnd: () => { dragRef.current = null; setDragOverIndex(null); },
                onClick: () => trySelect(q),
              };
              if (sec) {
                return (
                  <div key={qid || idx} {...commonDrag} style={{ cursor: 'pointer', borderTop: dropTop ? `2px solid ${COLORS.ember}` : '2px solid transparent', opacity: hidden ? 0.55 : 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 10, background: '#F4F2FF', border: sel ? `1.5px solid ${COLORS.indigo}` : '1.5px solid transparent' }}>
                      <Svg name="grip" size={16} color="#B3AEDD" />
                      <Svg name="layers" size={15} color={COLORS.indigoDeep} />
                      <div style={{ fontSize: 15, fontWeight: 700, color: text ? COLORS.indigoDeep : '#B3AEDD', flex: 1 }}>{text || 'Bölüm başlığı giriniz'}</div>
                      {hidden && <Svg name="eyeOff" size={14} color="#B3AEDD" />}
                    </div>
                  </div>
                );
              }
              return (
                <div key={qid || idx} {...commonDrag} style={{ cursor: 'pointer', borderTop: dropTop ? `2px solid ${COLORS.ember}` : '2px solid transparent', opacity: hidden ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', borderRadius: 12, overflow: 'hidden', border: sel ? `1.5px solid ${COLORS.indigo}` : `1.5px solid ${COLORS.line}`, background: COLORS.white, boxShadow: sel ? '0 4px 14px rgba(79,70,229,0.10)' : 'none' }}>
                    <div style={{ flex: 1, padding: '16px 18px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
                        <TypeBadge type={type} size={26} />
                        <div style={{ fontSize: 14.5, fontWeight: 600, color: text ? COLORS.ink : COLORS.muted, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text || 'Soru metninizi buraya giriniz'}</div>
                        {SELECTION_TYPES.includes(type) && <Pill tone="indigo">{(pick(previewQ, 'selectionMode', 'SelectionMode') === 'Multi') ? 'Çoklu' : 'Tekli'}</Pill>}
                        {required && <Pill tone="ember">Zorunlu</Pill>}
                        {hidden && <Svg name="eyeOff" size={14} color="#B3AEDD" />}
                      </div>
                      <FieldPreview q={previewQ} />
                    </div>
                    <div style={{ width: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', background: sel ? COLORS.indigoSoft : '#F6F5FB', cursor: 'grab', flexShrink: 0 }}>
                      <Svg name="grip" size={15} color={sel ? COLORS.indigo : '#B3AEDD'} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sağ panel */}
      <div style={{ position: 'sticky', top: 8, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!form ? (
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: '36px 22px', textAlign: 'center', color: COLORS.muted }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, opacity: 0.6 }}><Svg name="settings" size={26} color="#8B86B5" /></div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#8B86B5' }}>Soru ayarları</div>
            <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>Düzenlemek için kanvastaki bir soruyu seçin</div>
          </div>
        ) : (
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
              <TypeBadge type={form.questionType} size={28} />
              <div style={{ fontSize: 14.5, fontWeight: 700, color: COLORS.ink, flex: 1 }}>Soru ayarları</div>
              <span style={{ fontSize: 11, color: savingState === 'saving' ? '#a16207' : (dirty ? '#b91c1c' : COLORS.good) }}>
                {savingState === 'saving' ? 'Kaydediliyor…' : dirty ? 'Kaydedilmedi' : savingState === 'saved' ? 'Kaydedildi' : ''}
              </span>
            </div>

            <label style={labelStyle}>{isSection ? 'Başlık metni' : 'Soru metni'}</label>
            <input value={form.questionText} onChange={(e) => setF({ questionText: e.target.value })}
              placeholder={isSection ? 'Bölüm başlığı' : 'Soru metnini girin'} style={fieldInputStyle} />

            {showSelection && (
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>Seçim yapısı</label>
                <div style={{ display: 'flex', gap: 6, background: '#F4F2FF', borderRadius: 9, padding: 3 }}>
                  {[{ v: 'Single', l: 'Tekli seçim' }, { v: 'Multi', l: 'Çoklu seçim' }].map((o) => (
                    <button key={o.v} type="button" onClick={() => setF({ selectionMode: o.v })}
                      style={{ flex: 1, padding: '6px', borderRadius: 7, border: 'none', background: form.selectionMode === o.v ? COLORS.white : 'transparent', color: form.selectionMode === o.v ? COLORS.indigo : '#8B86B5', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', boxShadow: form.selectionMode === o.v ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{o.l}</button>
                  ))}
                </div>
              </div>
            )}

            {showOptions && (
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>Seçenekler</label>
                {form.optionList.map((o, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7 }}>
                    {form.selectionMode === 'Multi'
                      ? <Svg name="checkSquare" size={14} color="#C2BEDC" />
                      : <span style={{ width: 14, height: 14, borderRadius: '50%', border: '1.5px solid #C2BEDC', flexShrink: 0 }} />}
                    <input value={o.text} onChange={(e) => updateOption(i, e.target.value)} placeholder={`Seçenek ${i + 1}`} style={{ ...fieldInputStyle, flex: 1 }} />
                    <button type="button" onClick={() => removeOption(i)} title="Sil" style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 5, display: 'flex', flexShrink: 0 }}><Svg name="trash" size={13} color={COLORS.danger} /></button>
                  </div>
                ))}
                <button type="button" onClick={addOption} style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: COLORS.indigo, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}><Svg name="plus" size={14} color={COLORS.indigo} /> Seçenek ekle</button>
              </div>
            )}

            {showLinked && (() => {
              const isUser = form.questionType === 'User';
              const lists = isUser ? sourceLists.user : sourceLists.product;
              return (
                <div style={{ marginTop: 14 }}>
                  <label style={labelStyle}>{isUser ? 'Kullanıcı listesi kaynağı' : 'Ürün listesi kaynağı'}</label>
                  <select value={form.sourceListId || 'all'} onChange={(e) => setF({ sourceListId: e.target.value })} style={fieldInputStyle}>
                    {lists.length === 0 && <option value="all">{LINKED_SOURCE_LABEL[form.questionType]}</option>}
                    {lists.map((l) => <option key={l.id} value={l.id}>{l.name}{typeof l.count === 'number' ? ` (${l.count})` : ''}</option>)}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#8B86B5' }}><Svg name="link" size={12} color="#8B86B5" /> Seçili liste sahada dinamik beslenir</div>
                </div>
              );
            })()}

            {form.questionType === 'Photo' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>Çoklu görsel</span>
                <Toggle checked={form.multiplePhoto} onChange={(v) => setF({ multiplePhoto: v })} />
              </div>
            )}

            {showAvailability && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>Fotoğraf zorunlu mu?</span>
                  <Toggle checked={form.photoRequired} onChange={(v) => setF({ photoRequired: v })} />
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: COLORS.inkSoft, background: COLORS.indigoSoft, border: `1px solid ${COLORS.indigoBorder}`, borderRadius: 8, padding: '8px 10px' }}>
                  "Var" cevabında açılan foto alanını etkiler; sorunun genel "Zorunlu alan" ayarından bağımsızdır.
                </div>
              </div>
            )}

            {form.questionType === 'Money' && (
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>Para birimi</label>
                <select value={form.currency} onChange={(e) => setF({ currency: e.target.value })} style={fieldInputStyle}>
                  {CURRENCY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )}

            {showRange && (
              <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={labelStyle}>Min</label><input type="number" value={form.minValue} onChange={(e) => setF({ minValue: e.target.value })} style={fieldInputStyle} /></div>
                <div style={{ flex: 1 }}><label style={labelStyle}>Max</label><input type="number" value={form.maxValue} onChange={(e) => setF({ maxValue: e.target.value })} style={fieldInputStyle} /></div>
              </div>
            )}

            {TYPE_HINT[form.questionType] && (
              <div style={{ marginTop: 14, fontSize: 12, color: COLORS.inkSoft, background: COLORS.indigoSoft, border: `1px solid ${COLORS.indigoBorder}`, borderRadius: 8, padding: '8px 10px' }}>{TYPE_HINT[form.questionType]}</div>
            )}

            <div style={{ height: 1, background: COLORS.line, margin: '18px 0 14px' }} />

            {!isSection && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>Zorunlu alan</span>
                <Toggle checked={form.isRequired} onChange={(v) => setF({ isRequired: v })} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.ink }}>Görünsün</span>
              <Toggle checked={form.isVisible} onChange={(v) => setF({ isVisible: v })} />
            </div>
            {!isSection && form.isRequired && !form.isVisible && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
                Bu soru görünmüyor; "Zorunlu" ayarı etki yaratmayacak.
              </div>
            )}

            {!readOnly && (
              <>
                <button type="button" onClick={saveCurrent} disabled={!dirty || savingState === 'saving'}
                  style={{ width: '100%', marginTop: 18, padding: '10px', borderRadius: 8, border: 'none', background: (dirty && savingState !== 'saving') ? COLORS.indigo : '#c7c5e0', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: (dirty && savingState !== 'saving') ? 'pointer' : 'default' }}>
                  {savingState === 'saving' ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button type="button" onClick={duplicateSelected} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, border: `1px solid ${COLORS.indigoBorder}`, background: COLORS.white, color: COLORS.indigo, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Svg name="copy" size={13} color={COLORS.indigo} /> Çoğalt</button>
                  <button type="button" onClick={deleteSelected} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px', borderRadius: 8, border: `1px solid #f5d6db`, background: COLORS.white, color: COLORS.danger, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Svg name="trash" size={13} color={COLORS.danger} /> Soruyu sil</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* PreviewSummary */}
        <div style={{ background: COLORS.white, border: `1px solid ${COLORS.line}`, borderRadius: 14, padding: 16 }}>
          {[
            ['Toplam soru', summary.total],
            ['Zorunlu soru', summary.required],
            ['Bölüm başlığı', summary.sections],
            ['Bağlantılı alan', summary.linked],
          ].map(([label, value], i) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 3 ? `1px solid ${COLORS.line}` : 'none', fontSize: 13 }}>
              <span style={{ color: COLORS.inkSoft }}>{label}</span><span style={{ fontWeight: 700, color: COLORS.ink }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
